import { useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/shared';
import { useAuth } from '@/contexts/AuthContext';
import { useCollaboratorsPublicStaticQuery } from '@/features/Collaborators/Data';
import { COMENTARIO_SOLICITACAO_MAX_CARACTERES } from '../../../../models';
import { useComentariosSolicitacaoQuery, useComentarioSolicitacaoMutations } from '../../../../hooks/data';
import ComentarioItem from './ComentarioItem';

interface ObservacoesSolicitacaoSectionProps {
  solicitacaoId: string;
  /** Texto único de antes das postagens (Solicitacao.observacoes) — só leitura. */
  observacaoAnterior?: string;
}

/**
 * Observações gerais da Solicitação em postagens (como o acompanhamento de um chamado no
 * GLPI): cada um posta, fica registrado quem e quando, e o autor pode editar ou excluir a
 * própria. Quem vê a Solicitação pode postar (ver firestore.rules, subcoleção comentarios).
 */
export default function ObservacoesSolicitacaoSection({ solicitacaoId, observacaoAnterior }: ObservacoesSolicitacaoSectionProps) {
  const { user } = useAuth();
  const { data: comentarios = [], isLoading } = useComentariosSolicitacaoQuery(solicitacaoId);
  const { postar, editar, excluir } = useComentarioSolicitacaoMutations(solicitacaoId);
  // Foto do perfil de quem postou (collaborators_public, legível por qualquer logado) — por
  // id e, se não achar, por e-mail. Sem foto (ou autor inativo), o avatar mostra as iniciais.
  const { data: colaboradores = [] } = useCollaboratorsPublicStaticQuery();
  const fotoPorAutor = useMemo(() => {
    const mapa = new Map<string, string>();
    colaboradores.forEach((c) => {
      if (!c.photoUrl) return;
      mapa.set(c.id, c.photoUrl);
      if (c.email) mapa.set(c.email.toLowerCase(), c.photoUrl);
    });
    return mapa;
  }, [colaboradores]);
  const [novoTexto, setNovoTexto] = useState('');
  const [excluindoId, setExcluindoId] = useState<string | null>(null);

  // Erros já viram toast no onError das mutations — aqui só não limpa/fecha se falhar.
  const handlePostar = async () => {
    try {
      await postar.mutateAsync(novoTexto);
      setNovoTexto('');
    } catch {
      /* toast no onError */
    }
  };

  const handleConfirmarExclusao = async () => {
    if (!excluindoId) return;
    try {
      await excluir.mutateAsync(excluindoId);
      setExcluindoId(null);
    } catch {
      /* toast no onError */
    }
  };

  return (
    <div className="rounded-md border border-gray-200 p-4">
      <p className="text-sm font-medium text-gray-700">Observações</p>
      <p className="text-xs text-gray-500">
        Acompanhamento geral da solicitação (ex.: pedi os acessos ao responsável, chamei o colaborador e não tive
        resposta).
      </p>

      <ul className="mt-3 space-y-2">
        {observacaoAnterior?.trim() && (
          <li className="rounded-md border border-dashed border-gray-200 p-3">
            <p className="text-xs text-gray-500">Observação anterior (registrada antes das postagens)</p>
            <p className="mt-1 whitespace-pre-line text-sm text-gray-700">{observacaoAnterior}</p>
          </li>
        )}
        {comentarios.map((comentario) => (
          <ComentarioItem
            key={comentario.id}
            comentario={comentario}
            fotoUrl={
              fotoPorAutor.get(comentario.autor.id) ??
              fotoPorAutor.get(comentario.autorEmail.toLowerCase()) ??
              comentario.autor.avatar ??
              null
            }
            ehAutor={Boolean(user?.email) && comentario.autorEmail === user?.email}
            salvando={editar.isPending}
            onSalvarEdicao={(texto) => editar.mutateAsync({ comentarioId: comentario.id, texto })}
            onExcluir={() => setExcluindoId(comentario.id)}
          />
        ))}
        {!isLoading && comentarios.length === 0 && !observacaoAnterior?.trim() && (
          <li className="text-sm text-gray-400">Nenhuma observação ainda.</li>
        )}
      </ul>

      <div className="mt-3 space-y-2">
        <textarea
          className="input-primary w-full"
          rows={3}
          placeholder="Escreva uma observação..."
          maxLength={COMENTARIO_SOLICITACAO_MAX_CARACTERES}
          value={novoTexto}
          onChange={(event) => setNovoTexto(event.target.value)}
        />
        <button
          type="button"
          className="btn-secondary text-sm"
          onClick={handlePostar}
          disabled={postar.isPending || !novoTexto.trim()}
        >
          Enviar
        </button>
      </div>

      <ConfirmDialog
        isOpen={excluindoId != null}
        title="Excluir observação"
        message="A observação será apagada e não poderá ser recuperada. Deseja continuar?"
        type="danger"
        confirmText="Excluir"
        confirmando={excluir.isPending}
        onCancel={() => setExcluindoId(null)}
        onConfirm={handleConfirmarExclusao}
      />
    </div>
  );
}
